<template>
  <div class="salon-container">
    <!-- Aurora orbs -->
    <div class="aurora-orb aurora-orb-1"></div>
    <div class="aurora-orb aurora-orb-2"></div>
    <div class="aurora-orb aurora-orb-3"></div>

    <!-- Main glass card -->
    <div class="salon-card">
      <!-- Welcome header -->
      <div class="salon-header" v-if="!submitted">
        <div class="header-eyebrow">Salone del Design</div>
        <h1 class="header-title">Benvenuto</h1>
        <p class="step-question">{{ stepTitles[currentStep] }}</p>
      </div>

      <!-- Success state -->
      <div class="success-container" v-if="submitted">
        <div class="checkmark-circle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <h2 class="success-title">Grazie!</h2>
        <p class="success-text">La tua registrazione è avvenuta con successo.</p>
      </div>

      <!-- Form steps -->
      <transition name="slide-fade" v-if="!submitted">
        <div :key="currentStep" class="step-container">
          <!-- Step 0: Nome -->
          <div v-if="currentStep === 0" class="input-wrapper">
            <input v-model="nome" type="text" class="glass-input" placeholder="Mario"
              @keyup.enter="nextStep" ref="inputRef" autocomplete="given-name" />
          </div>

          <!-- Step 1: Cognome -->
          <div v-if="currentStep === 1" class="input-wrapper">
            <input v-model="cognome" type="text" class="glass-input" placeholder="Rossi"
              @keyup.enter="nextStep" ref="inputRef" autocomplete="family-name" />
          </div>

          <!-- Step 2: Company -->
          <div v-if="currentStep === 2" class="input-wrapper">
            <input v-model="company" type="text" class="glass-input" placeholder="Design Studio Co."
              @keyup.enter="nextStep" ref="inputRef" autocomplete="organization" />
            <span class="optional-hint">facoltativo</span>
          </div>

          <!-- Step 3: Email + Privacy -->
          <div v-if="currentStep === 3" class="input-wrapper">
            <input v-model="email" type="email" class="glass-input" placeholder="nome@example.com"
              @keyup.enter="submitForm" ref="inputRef" autocomplete="email" />
            <div class="privacy-wrapper">
              <label class="privacy-toggle">
                <input v-model="privacyAccepted" type="checkbox" class="privacy-checkbox" />
                <span class="toggle-track"></span>
                <span class="privacy-text">
                  Accetto la
                  <a href="https://www.iubenda.com/privacy-policy/16268737" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
          </div>
        </div>
      </transition>

      <!-- Step indicator -->
      <div class="step-indicator" v-if="!submitted">
        <div v-for="i in 4" :key="i" class="step-dot" :class="{ active: currentStep >= i - 1 }"></div>
      </div>

      <!-- Navigation buttons -->
      <div class="button-group" v-if="!submitted">
        <button v-if="currentStep > 0" @click="prevStep" class="btn btn-back">
          ← Indietro
        </button>

        <button v-if="currentStep < 3" @click="nextStep" :disabled="!canProceed" class="btn btn-next">
          Avanti →
        </button>

        <button v-if="currentStep === 3" @click="submitForm()" :disabled="!canSubmit" class="btn btn-submit"
          :class="{ 'btn-loading': isLoading }">
          {{ isLoading ? 'Attendere...' : 'Registrati' }}
        </button>
      </div>

      <!-- Error message -->
      <div class="error-message" v-if="errorMessage">
        {{ errorMessage }}
      </div>

      <!-- AI Voice button -->
      <div class="ai-button-wrapper" v-if="!submitted">
        <button class="btn-ai" @click="openAI">
          <span class="ai-icon">🎙️</span>
          Registrazione con Assistente AI
        </button>
      </div>
    </div>

    <!-- AI Voice Modal -->
    <div v-if="aiMode" class="ai-overlay">
      <button class="ai-close" @click="closeAI">✕</button>

      <div class="ai-content">
        <div class="ai-avatar" :class="{ listening: isListening, speaking: isSpeaking }">
          <div class="ai-pulse"></div>
          <span class="ai-avatar-icon">{{ isSpeaking ? '🔊' : isListening ? '👂' : '🎙️' }}</span>
        </div>

        <!-- Lingua rilevata automaticamente -->
        <div class="lang-indicator" v-if="aiStarted">
          {{ lang === 'it' ? '🇮🇹 Italiano' : '🇬🇧 English' }}
        </div>

        <div class="ai-status">
          {{ isSpeaking
            ? (lang === 'it' ? 'L\'assistente sta parlando...' : 'Assistant is speaking...')
            : isListening
              ? (lang === 'it' ? 'In ascolto...' : 'Listening...')
              : (lang === 'it' ? 'Pronto' : 'Ready') }}
        </div>

        <div class="ai-transcript" ref="transcriptBox">
          <div
            v-for="(msg, i) in aiMessages"
            :key="i"
            class="ai-message"
            :class="msg.role === 'assistant' ? 'ai-msg-assistant' : 'ai-msg-user'"
          >
            <span class="msg-label">{{ msg.role === 'assistant' ? 'Assistente' : 'Tu' }}</span>
            <span class="msg-text">{{ msg.content }}</span>
          </div>
        </div>

        <!-- Tocca per avviare (solo al primo avvio) -->
        <button
          v-if="!aiStarted"
          class="btn-speak"
          @click="firstStart"
        >
          🎙️ Tocca per iniziare
        </button>

        <div class="ai-error" v-if="aiError">{{ aiError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SalonRegistration',
  data() {
    return {
      currentStep: 0,
      nome: '',
      cognome: '',
      company: '',
      email: '',
      privacyAccepted: false,
      submitted: false,
      isLoading: false,
      errorMessage: '',
      stepTitles: [
        'Come ti chiami?',
        'Qual è il tuo cognome?',
        'Da quale azienda vieni?',
        'Qual è la tua email?'
      ],
      // AI Modal
      aiMode: false,
      isListening: false,
      isSpeaking: false,
      aiReady: false,
      aiStarted: false,      // true dopo il primo tap (sblocca audio iOS)
      langDetected: false,   // true dopo il primo rilevamento lingua
      lang: 'it',            // 'it' | 'en' — rilevato automaticamente
      aiMessages: [],
      aiError: '',
      recognition: null,
      synth: window.speechSynthesis
    };
  },
  computed: {
    canProceed() {
      if (this.currentStep === 0) return this.nome.trim().length > 0;
      if (this.currentStep === 1) return this.cognome.trim().length > 0;
      if (this.currentStep === 2) return true;
      return false;
    },
    canSubmit() {
      return (
        this.email.trim().length > 0 &&
        this.isValidEmail(this.email) &&
        this.privacyAccepted
      );
    }
  },
  methods: {
    // ── Form methods ────────────────────────────────────
    nextStep() {
      if (!this.canProceed) {
        this.errorMessage = 'Completa il campo per proseguire';
        setTimeout(() => { this.errorMessage = ''; }, 2000);
        return;
      }
      if (this.currentStep < 3) {
        this.currentStep++;
        this.errorMessage = '';
        this.$nextTick(() => {
          if (this.$refs.inputRef) this.$refs.inputRef.focus();
        });
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.errorMessage = '';
        this.$nextTick(() => {
          if (this.$refs.inputRef) this.$refs.inputRef.focus();
        });
      }
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    async submitForm(dataFromAI = null) {
      const payload = dataFromAI || {
        nome: this.nome.trim(),
        cognome: this.cognome.trim(),
        company: this.company.trim(),
        email: this.email.trim()
      };

      if (!dataFromAI) {
        if (!this.canSubmit) {
          this.errorMessage = 'Completa tutti i campi obbligatori';
          return;
        }
        this.isLoading = true;
        this.errorMessage = '';
      }

      try {
        const response = await axios.post(
          process.env.VUE_APP_SERVER3 + '/salon/register',
          payload
        );
        if (response.status === 200) {
          if (dataFromAI) this.closeAI();
          this.submitted = true;
          setTimeout(() => { this.resetForm(); }, 4000);
        }
      } catch (error) {
        console.error('Errore registrazione:', error);
        if (dataFromAI) {
          this.aiError = 'Errore durante la registrazione. Riprova.';
        } else {
          this.errorMessage = 'Errore durante la registrazione. Riprova.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.nome = '';
      this.cognome = '';
      this.company = '';
      this.email = '';
      this.privacyAccepted = false;
      this.currentStep = 0;
      this.submitted = false;
      this.errorMessage = '';
    },

    // ── AI methods ──────────────────────────────────────
    openAI() {
      this.aiMode = true;
      this.aiStarted = false;
      this.aiReady = false;
      this.langDetected = false;
      this.lang = 'it';
      this.aiMessages = [];
      this.aiError = '';
    },
    // Rileva la lingua dal testo con semplice euristica
    detectLanguage(text) {
      const t = text.toLowerCase();
      const itPattern = /\b(il|la|lo|le|gli|un|una|di|da|in|con|su|per|tra|fra|sono|mi|mio|mia|ciao|buon|grazie|prego|sì|si|nome|come|qual|azienda|email|lavoro)\b/g;
      const enPattern = /\b(the|is|are|am|my|your|i|we|you|he|she|they|a|an|of|and|or|but|hello|hi|yes|thank|please|name|company|work|from|what)\b/g;
      const itScore = (t.match(itPattern) || []).length;
      const enScore = (t.match(enPattern) || []).length;
      return enScore > itScore ? 'en' : 'it';
    },
    closeAI() {
      this.stopListening();
      this.synth.cancel();
      this.aiMode = false;
      this.isListening = false;
      this.isSpeaking = false;
      this.aiReady = false;
      this.aiStarted = false;
      this.aiMessages = [];
      this.aiError = '';
    },
    // Primo tap: sblocca audio iOS + avvia tutto nel contesto del gesto utente
    firstStart() {
      // Unlock audio context iOS — deve stare dentro il gestore del tap
      const unlock = new SpeechSynthesisUtterance('');
      this.synth.speak(unlock);

      this.aiStarted = true;
      this.lang = 'en'; // saluto in inglese, poi switcha in base alla risposta

      const greeting = 'Welcome to the Design Salon! Would you prefer to speak in Italian or in English?';
      this.aiMessages.push({ role: 'assistant', content: greeting });
      this.scrollTranscript();
      this.speak(greeting, () => {
        if (this.aiMode) this.restartListening();
      });
    },
    startListening() {
      const SpeechRecognition = (window.SpeechRecognition || window.webkitSpeechRecognition);
      if (!SpeechRecognition) {
        this.aiError = this.lang === 'en'
          ? '⚠️ Voice recognition is not supported on Firefox. Please use Chrome, Edge or Safari.'
          : '⚠️ Firefox non supporta il riconoscimento vocale. Usa Chrome, Edge o Safari (iPad).';
        return;
      }
      if (this.recognition) {
        try { this.recognition.abort(); } catch(e) { /* ignore */ }
      }

      this.aiError = '';
      let accumulatedText = '';
      let silenceTimer = null;

      const sendAccumulated = () => {
        clearTimeout(silenceTimer);
        silenceTimer = null;
        const text = accumulatedText.trim();
        accumulatedText = '';
        if (!text) return;
        this.stopListening();
        this.aiMessages.push({ role: 'user', content: text });
        this.scrollTranscript();
        this.callClaude(text);
      };

      this.recognition = new SpeechRecognition();
      // Prima della rilevazione usa sempre it-IT (riconosce sia "italiano" sia "english")
      // Dopo la rilevazione usa la lingua corretta
      this.recognition.lang = (this.langDetected && this.lang === 'en') ? 'en-US' : 'it-IT';
      this.recognition.continuous = true;      // accumula frasi con pause
      this.recognition.interimResults = true;  // rileva quando l'utente smette

      this.recognition.onstart = () => { this.isListening = true; };
      this.recognition.onend   = () => {
        this.isListening = false;
        // Se è finita con testo accumulato, invialo
        if (accumulatedText.trim()) sendAccumulated();
      };

      this.recognition.onresult = (event) => {
        let finalText = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalText += event.results[i][0].transcript + ' ';
          }
        }
        if (finalText.trim()) {
          // Auto-detect lingua al primo messaggio
          if (!this.langDetected) {
            this.langDetected = true;
            const detected = this.detectLanguage(finalText);
            if (detected !== this.lang) {
              this.lang = detected;
              // Aggiorna la lingua del recognition per i turni successivi
              this.recognition.lang = detected === 'en' ? 'en-US' : 'it-IT';
            }
          }
          accumulatedText += finalText;
          clearTimeout(silenceTimer);
          silenceTimer = setTimeout(sendAccumulated, 1800);
        }
      };

      this.recognition.onerror = (event) => {
        this.isListening = false;
        clearTimeout(silenceTimer);
        if (event.error === 'aborted') return; // ignorato: siamo noi a chiamare abort()
        if (event.error === 'no-speech') {
          if (accumulatedText.trim()) sendAccumulated();
          else if (this.aiMode && !this.isSpeaking) this.restartListening();
        } else {
          this.aiError = 'Errore microfono: ' + event.error;
        }
      };

      try { this.recognition.start(); } catch(e) { /* già in corso */ }
    },
    restartListening() {
      // Riavvio automatico dopo risposta AI — aspetta 800ms per far svanire l'eco della voce
      if (!this.aiMode || this.isSpeaking) return;
      setTimeout(() => {
        if (this.aiMode && !this.isSpeaking && !this.isListening) {
          this.startListening();
        }
      }, 800);
    },
    stopListening() {
      if (this.recognition) {
        try { this.recognition.abort(); } catch(e) { /* ignore */ }
        this.recognition = null;
      }
      this.isListening = false;
    },
    async callClaude(userText) {
      // Aggiungi il messaggio utente allo storico (evita duplicati dal startListening)
      const lastMsg = this.aiMessages[this.aiMessages.length - 1];
      if (!lastMsg || lastMsg.role !== 'user' || lastMsg.content !== userText) {
        this.aiMessages.push({ role: 'user', content: userText });
      }

      try {
        const response = await axios.post(
          process.env.VUE_APP_SERVER3 + '/salon/ai-chat',
          { messages: this.aiMessages, lang: this.lang }
        );

        const { message, submit } = response.data;
        this.aiMessages.push({ role: 'assistant', content: message });
        this.scrollTranscript();

        // Se Claude ha restituito i dati → registra automaticamente
        if (submit) {
          const thankYou = this.lang === 'en'
            ? 'Perfect, I will proceed with your registration. Thank you!'
            : 'Perfetto, procedo con la registrazione. Grazie!';
          this.speak(thankYou);
          setTimeout(() => { this.submitForm(submit); }, 1500);
          return;
        }

        // Altrimenti leggi la risposta e riprendi ad ascoltare automaticamente
        this.speak(message, () => {
          if (this.aiMode) this.restartListening();
        });

      } catch (err) {
        console.error('Errore AI:', err);
        this.aiError = 'Errore di connessione con l\'assistente AI.';
      }
    },
    async speak(text, onEnd) {
      this.isSpeaking = true;
      try {
        const response = await axios.post(
          process.env.VUE_APP_SERVER3 + '/salon/tts',
          { text, lang: this.lang },
          { responseType: 'blob' }
        );

        const audioUrl = URL.createObjectURL(response.data);
        const audio = new Audio(audioUrl);

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          this.isSpeaking = false;
          if (onEnd) onEnd();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          this.isSpeaking = false;
          if (onEnd) onEnd();
        };

        await audio.play();
      } catch (err) {
        console.error('Errore Piper TTS:', err);
        // Fallback: usa il browser TTS
        this.isSpeaking = true;
        const utter = new SpeechSynthesisUtterance(text);
        utter.lang = 'it-IT';
        utter.rate = 1.05;
        const voices = this.synth.getVoices();
        const itVoice = voices.find(v => v.lang.startsWith('it'));
        if (itVoice) utter.voice = itVoice;
        utter.onend = () => { this.isSpeaking = false; if (onEnd) onEnd(); };
        utter.onerror = () => { this.isSpeaking = false; if (onEnd) onEnd(); };
        this.synth.speak(utter);
      }
    },
    scrollTranscript() {
      this.$nextTick(() => {
        const box = this.$refs.transcriptBox;
        if (box) box.scrollTop = box.scrollHeight;
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.inputRef) this.$refs.inputRef.focus();
    });
  },
  beforeUnmount() {
    this.stopListening();
    this.synth.cancel();
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

/* ── Base container ──────────────────────────────────── */
.salon-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #0d0d1a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
  font-family: 'Georgia', serif;
  z-index: 9999;
}

/* ── Aurora orbs ─────────────────────────────────────── */
.aurora-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: floatOrb 12s ease-in-out infinite;
}
.aurora-orb-1 {
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(100, 50, 200, 0.55) 0%, transparent 70%);
  top: -150px; left: -150px; animation-duration: 14s;
}
.aurora-orb-2 {
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(0, 180, 180, 0.45) 0%, transparent 70%);
  bottom: -100px; right: -100px; animation-duration: 18s; animation-delay: -6s;
}
.aurora-orb-3 {
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(60, 80, 220, 0.35) 0%, transparent 70%);
  top: 40%; left: 50%; transform: translate(-50%, -50%);
  animation-duration: 22s; animation-delay: -3s;
}
@keyframes floatOrb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(30px, -40px) scale(1.05); }
  66%       { transform: translate(-20px, 30px) scale(0.95); }
}

/* ── Glass card ──────────────────────────────────────── */
.salon-card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 480px;
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 24px;
  padding: 56px 48px 40px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06) inset;
}

/* ── Header ──────────────────────────────────────────── */
.salon-header { text-align: center; margin-bottom: 48px; }
.header-eyebrow {
  font-size: 0.75rem; letter-spacing: 4px; text-transform: uppercase;
  color: rgba(160, 200, 255, 0.7); margin-bottom: 10px;
  font-family: 'Arial', sans-serif; font-weight: 300;
}
.header-title { font-size: 2.6rem; font-weight: 300; color: #fff; margin: 0 0 18px; letter-spacing: 2px; }
.step-question { font-size: 1.05rem; color: rgba(200,220,255,0.85); margin: 0; font-weight: 300; font-style: italic; }

/* ── Success ─────────────────────────────────────────── */
.success-container { text-align: center; padding: 40px 20px; animation: fadeScaleIn 0.6s cubic-bezier(0.22,1,0.36,1); }
@keyframes fadeScaleIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
.checkmark-circle {
  width: 80px; height: 80px; margin: 0 auto 28px; border-radius: 50%;
  background: linear-gradient(135deg, rgba(100,200,180,0.3), rgba(80,140,255,0.3));
  border: 1.5px solid rgba(150,220,200,0.5);
  display: flex; align-items: center; justify-content: center;
  animation: pulseCheck 2s ease-in-out infinite;
}
.checkmark-circle svg { width: 36px; height: 36px; stroke: #a0f0d8; filter: drop-shadow(0 0 6px rgba(160,240,216,0.8)); }
@keyframes pulseCheck { 0%,100% { box-shadow: 0 0 0 0 rgba(100,200,180,0.3); } 50% { box-shadow: 0 0 0 14px rgba(100,200,180,0); } }
.success-title { font-size: 2rem; font-weight: 300; color: #fff; margin: 0 0 12px; letter-spacing: 2px; }
.success-text { color: rgba(200,220,255,0.75); font-size: 0.95rem; margin: 0; font-style: italic; }

/* ── Step container ──────────────────────────────────── */
.step-container { min-height: 120px; display: flex; flex-direction: column; justify-content: center; }
.input-wrapper { display: flex; flex-direction: column; gap: 8px; }
.glass-input {
  width: 100%; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.18);
  border-radius: 12px; color: #fff; font-size: 1.25rem; font-family: 'Georgia', serif;
  font-weight: 300; padding: 18px 22px; outline: none;
  transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
}
.glass-input::placeholder { color: rgba(180,200,240,0.35); }
.glass-input:focus { border-color: rgba(140,180,255,0.6); background: rgba(255,255,255,0.1); box-shadow: 0 0 0 3px rgba(120,160,255,0.15); }
.optional-hint { font-size: 0.75rem; color: rgba(160,180,220,0.5); letter-spacing: 1px; font-family: 'Arial', sans-serif; padding-left: 4px; }

/* ── Privacy toggle ──────────────────────────────────── */
.privacy-wrapper { margin-top: 20px; padding-top: 18px; border-top: 1px solid rgba(255,255,255,0.1); }
.privacy-toggle { display: flex; align-items: center; gap: 14px; cursor: pointer; }
.privacy-checkbox { display: none; }
.toggle-track {
  width: 44px; height: 24px; border-radius: 12px;
  background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
  flex-shrink: 0; position: relative; transition: background 0.3s ease;
}
.toggle-track::after {
  content: ''; position: absolute; width: 18px; height: 18px; border-radius: 50%;
  background: rgba(160,180,220,0.6); top: 2px; left: 2px;
  transition: transform 0.3s ease, background 0.3s ease;
}
.privacy-checkbox:checked + .toggle-track { background: rgba(100,160,255,0.35); border-color: rgba(120,180,255,0.5); }
.privacy-checkbox:checked + .toggle-track::after { transform: translateX(20px); background: #a0c8ff; }
.privacy-text { font-size: 0.82rem; color: rgba(190,210,250,0.75); font-family: 'Arial', sans-serif; font-weight: 300; line-height: 1.5; }
.privacy-text a { color: rgba(160,200,255,0.9); text-decoration: underline; text-underline-offset: 2px; }
.privacy-text a:hover { color: #fff; }

/* ── Step dots ───────────────────────────────────────── */
.step-indicator { display: flex; justify-content: center; gap: 10px; margin: 38px 0; }
.step-dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.2); transition: all 0.4s cubic-bezier(0.22,1,0.36,1); }
.step-dot.active { background: rgba(140,190,255,0.8); border-color: rgba(160,210,255,0.9); box-shadow: 0 0 8px rgba(140,190,255,0.5); transform: scale(1.2); }

/* ── Buttons ─────────────────────────────────────────── */
.button-group { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
.btn {
  padding: 14px 32px; min-height: 52px; border-radius: 50px; font-size: 0.88rem;
  font-family: 'Arial', sans-serif; font-weight: 400; letter-spacing: 1.5px; cursor: pointer;
  transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
}
.btn:disabled { opacity: 0.35; cursor: not-allowed; }
.btn-back { background: transparent; border: 1px solid rgba(255,255,255,0.2); color: rgba(200,210,240,0.7); }
.btn-back:hover:not(:disabled) { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.35); color: rgba(220,230,255,0.9); }
.btn-next { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.25); color: #fff; }
.btn-next:hover:not(:disabled) { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.45); transform: translateY(-2px); box-shadow: 0 6px 20px rgba(100,140,255,0.2); }
.btn-submit { background: linear-gradient(135deg, rgba(100,130,255,0.4) 0%, rgba(80,190,190,0.3) 100%); border: 1px solid rgba(140,180,255,0.4); color: #fff; box-shadow: 0 4px 16px rgba(80,120,220,0.25); }
.btn-submit:hover:not(:disabled) { background: linear-gradient(135deg, rgba(120,150,255,0.55) 0%, rgba(80,200,200,0.45) 100%); border-color: rgba(160,200,255,0.6); transform: translateY(-2px); box-shadow: 0 8px 28px rgba(80,120,220,0.35); }
.btn-loading { animation: breathe 1.5s ease-in-out infinite; }
@keyframes breathe { 0%,100% { opacity: 1; } 50% { opacity: 0.65; } }

/* ── Transitions ─────────────────────────────────────── */
.slide-fade-enter-active { animation: slideInRight 0.4s cubic-bezier(0.22,1,0.36,1); }
.slide-fade-leave-active { animation: slideOutLeft 0.35s cubic-bezier(0.55,0,1,0.45); }
@keyframes slideInRight { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: translateX(0); } }
@keyframes slideOutLeft { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-28px); } }

/* ── Error ───────────────────────────────────────────── */
.error-message {
  margin-top: 16px; text-align: center; background: rgba(255,80,80,0.12);
  border: 1px solid rgba(255,100,100,0.3); border-radius: 10px;
  color: rgba(255,160,160,0.9); padding: 10px 18px; font-size: 0.82rem;
  font-family: 'Arial', sans-serif; animation: shake 0.4s ease;
}
@keyframes shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-8px); } 75% { transform: translateX(8px); } }

/* ── AI button ───────────────────────────────────────── */
.ai-button-wrapper { display: flex; justify-content: center; margin-top: 24px; }
.btn-ai {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 28px; border-radius: 50px; cursor: pointer;
  background: rgba(120, 80, 255, 0.15);
  border: 1px solid rgba(160, 120, 255, 0.35);
  color: rgba(200, 180, 255, 0.9);
  font-family: 'Arial', sans-serif; font-size: 0.82rem; font-weight: 400;
  letter-spacing: 1px;
  transition: all 0.3s ease;
}
.btn-ai:hover { background: rgba(140, 100, 255, 0.28); border-color: rgba(180, 140, 255, 0.55); transform: translateY(-1px); }
.ai-icon { font-size: 1rem; }

/* ── AI Overlay ──────────────────────────────────────── */
.ai-overlay {
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(8, 8, 20, 0.96);
  backdrop-filter: blur(20px);
  display: flex; align-items: center; justify-content: center;
}
.ai-close {
  position: absolute; top: 1.5vh; right: 1.5vw;
  width: 40px; height: 40px; border-radius: 50%;
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2);
  color: #fff; font-size: 1rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s ease;
}
.ai-close:hover { background: rgba(255,60,60,0.4); }

/* ── AI content ──────────────────────────────────────── */
.ai-content {
  width: 100%; max-width: 480px; padding: 24px;
  display: flex; flex-direction: column; align-items: center; gap: 24px;
}

/* ── Avatar animato ──────────────────────────────────── */
.ai-avatar {
  position: relative; width: 120px; height: 120px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(120,80,255,0.3), rgba(60,40,140,0.2));
  border: 1.5px solid rgba(160,120,255,0.4);
  display: flex; align-items: center; justify-content: center;
  transition: all 0.4s ease;
}
.ai-avatar.listening { border-color: rgba(100,220,180,0.7); background: radial-gradient(circle, rgba(60,200,160,0.25), rgba(20,80,60,0.2)); }
.ai-avatar.speaking  { border-color: rgba(100,160,255,0.7); background: radial-gradient(circle, rgba(80,140,255,0.25), rgba(20,40,120,0.2)); }
.ai-pulse {
  position: absolute; inset: -8px; border-radius: 50%;
  border: 2px solid rgba(160,120,255,0.3);
  animation: pulseRing 2s ease-in-out infinite;
}
.ai-avatar.listening .ai-pulse { border-color: rgba(100,220,180,0.4); animation-duration: 1s; }
.ai-avatar.speaking  .ai-pulse { border-color: rgba(100,160,255,0.4); animation-duration: 0.7s; }
@keyframes pulseRing { 0%,100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.12); opacity: 0.2; } }
.ai-avatar-icon { font-size: 2.8rem; }

/* ── Status ──────────────────────────────────────────── */
.ai-status {
  font-family: 'Arial', sans-serif; font-size: 0.85rem; font-weight: 300;
  color: rgba(180,200,255,0.7); letter-spacing: 1px; text-transform: uppercase;
}

/* ── Transcript ──────────────────────────────────────── */
.ai-transcript {
  width: 100%; max-height: 280px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 12px;
  padding: 4px;
}
.ai-message { display: flex; flex-direction: column; gap: 3px; }
.msg-label { font-size: 0.68rem; letter-spacing: 1.5px; text-transform: uppercase; font-family: 'Arial', sans-serif; }
.msg-text { font-size: 0.95rem; line-height: 1.55; font-family: 'Georgia', serif; }
.ai-msg-assistant .msg-label { color: rgba(160,120,255,0.7); }
.ai-msg-assistant .msg-text  { color: rgba(220,210,255,0.92); }
.ai-msg-user .msg-label { color: rgba(100,200,180,0.7); text-align: right; }
.ai-msg-user .msg-text  { color: rgba(180,240,220,0.85); text-align: right; }

/* ── Indicatore lingua rilevata ──────────────────────── */
.lang-indicator {
  font-family: 'Arial', sans-serif;
  font-size: 0.75rem;
  letter-spacing: 1px;
  color: rgba(180, 210, 255, 0.6);
  padding: 4px 12px;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 20px;
}

/* ── Pulsante avvio (solo al primo tap) ──────────────── */
.btn-speak {
  width: 100%;
  padding: 18px 24px;
  border-radius: 50px;
  background: linear-gradient(135deg, rgba(100,200,160,0.25), rgba(60,140,220,0.2));
  border: 1.5px solid rgba(100,220,180,0.45);
  color: rgba(180,255,220,0.95);
  font-family: 'Arial', sans-serif;
  font-size: 1.1rem;
  font-weight: 400;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.25s ease;
  animation: breatheBtn 2s ease-in-out infinite;
}
.btn-speak:hover, .btn-speak:active {
  background: linear-gradient(135deg, rgba(100,220,180,0.4), rgba(60,160,240,0.35));
  border-color: rgba(120,240,200,0.65);
  transform: scale(1.02);
}
@keyframes breatheBtn {
  0%,100% { box-shadow: 0 0 0 0 rgba(100,200,160,0.2); }
  50%      { box-shadow: 0 0 0 10px rgba(100,200,160,0); }
}

/* ── AI error ────────────────────────────────────────── */
.ai-error {
  width: 100%; background: rgba(255,80,80,0.12); border: 1px solid rgba(255,100,100,0.3);
  border-radius: 10px; color: rgba(255,160,160,0.9); padding: 10px 16px;
  font-size: 0.82rem; font-family: 'Arial', sans-serif; text-align: center;
}

/* ── Responsive ──────────────────────────────────────── */
@media (max-height: 800px) {
  .salon-card { padding: 40px 36px 32px; }
  .header-title { font-size: 2rem; }
  .salon-header { margin-bottom: 32px; }
  .step-indicator { margin: 28px 0; }
}
@media (max-width: 600px) {
  .salon-container { padding: 12px; }
  .salon-card { border-radius: 18px; padding: 44px 28px 32px; }
  .header-title { font-size: 2rem; }
  .glass-input { font-size: 1.1rem; padding: 16px 18px; }
  .btn { flex: 1; min-width: 120px; }
}
</style>
